import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Alarm, Notebook, Sparkle } from '@phosphor-icons/react';

// /manifesto — The Anti-Salesforce Manifesto. Editorial letter from the
// founder. Source: Chip's own words (steamer story, six-months lead) +
// Emmy positioning session, Jul 2026.
export default function ManifestoPage() {
  useEffect(() => {
    document.title = 'The Anti-Salesforce Manifesto — SalesCrunchCRM';
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-800" data-testid="manifesto-page">
      {/* Minimal top bar */}
      <header className="border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="font-black text-slate-900 hover:text-[#0B5394]" data-testid="manifesto-home-link">
            SalesCrunch<span className="text-[#0B5394]">CRM</span>
          </Link>
          <Link
            to="/login"
            className="text-sm font-bold text-white bg-[#0B5394] hover:bg-[#094276] px-4 py-2 rounded"
            data-testid="manifesto-top-cta"
          >
            Try it — $30/user/mo
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-xs font-black uppercase tracking-[0.2em] text-[#CC6600] mb-3">A letter from our founder</p>
        <h1 className="text-4xl sm:text-5xl font-black text-slate-900 leading-tight mb-4" data-testid="manifesto-title">
          The Anti-Salesforce Manifesto
        </h1>
        <p className="text-lg text-slate-500 mb-12" data-testid="manifesto-subtitle">
          Why SalesCrunch has no pipelines, no lead scoring, and no stage gates &mdash; on purpose.
        </p>

        <section className="space-y-5 mb-12" data-testid="manifesto-section-pipelines">
          <h2 className="text-2xl font-black text-slate-900">Pipelines were built for sales managers, not salespeople.</h2>
          <p className="leading-relaxed">
            The logic made sense on paper: standardize the process, track every stage, push every lead
            through the same funnel. But in real selling, <strong>every lead is different.</strong> One says
            call me in six months. Another wants weekly updates. A third will buy when their budget resets
            in Q4. No pipeline template accounts for that.
          </p>
          <p className="leading-relaxed">
            So what happens? The lead says <em>&ldquo;call me in six months and I&rsquo;ll buy&rdquo;</em> &mdash;
            and the pipeline says <em>bother him every two weeks until he does.</em> The prospect gets called
            too early and feels pressured. The salesperson resents the tool that made them do it. The manager
            wonders why conversion rates are low.
          </p>
          <p className="text-xl font-bold text-slate-900 border-l-4 border-[#CC6600] pl-4">
            The note said everything you needed to know. Nobody read the note.
          </p>
        </section>

        <section className="space-y-5 mb-12" data-testid="manifesto-section-fault">
          <h2 className="text-2xl font-black text-slate-900">
            Salesforce doesn&rsquo;t lose deals. Salespeople do. And Salesforce made it their fault.
          </h2>
          <p className="leading-relaxed">
            Lead scoring. Activity logging. Stage gates. Mandatory fields before you&rsquo;re allowed to
            advance a contact. Call it what it is: <strong>a system designed to tell a salesperson they did
            their job wrong.</strong>
          </p>
          <p className="leading-relaxed">
            And while it judges you, it eats your day. Updating stages. Logging activities. Justifying to a
            dashboard why you haven&rsquo;t harassed the lead who explicitly told you to call back in six
            months. That&rsquo;s hours every week spent feeding the machine instead of talking to prospects.
          </p>
        </section>

        <section className="mb-12" data-testid="manifesto-section-steamers">
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 sm:p-8">
            <h2 className="text-2xl font-black text-slate-900 mb-4">I know, because I sold steamers.</h2>
            <div className="space-y-4 leading-relaxed">
              <p>
                I was the top salesperson at three companies. I was a CEO who still sold. And I&rsquo;ll tell
                you the thing nobody in the CRM industry will admit: <strong>leads go cold for reasons no
                software can fix.</strong> They find another solution. The budget changes. They decide they
                don&rsquo;t need it after all.
              </p>
              <p>
                I sold commercial cooking steamers. Some prospects who swore they&rsquo;d buy eventually
                decided they&rsquo;d just boil it on the stove. That&rsquo;s not a CRM failure.
                <strong> That&rsquo;s selling.</strong>
              </p>
              <p>
                You kill a dead lead eventually &mdash; every real salesperson does. But in thirty years,
                nobody on my teams got <em>forgotten</em>. Not because a CRM prompted us. Because we read
                the notes and set the alarm.
              </p>
            </div>
            <p className="mt-6 text-sm font-bold text-slate-500">&mdash; Chip, Founder of SalesCrunchCRM</p>
          </div>
        </section>

        <section className="space-y-5 mb-14" data-testid="manifesto-section-way">
          <h2 className="text-2xl font-black text-slate-900">The SalesCrunch way</h2>
          <p className="leading-relaxed">
            Read the note. Set the alarm. Call on the day they asked to be called. Kill the lead when
            it&rsquo;s dead. No stage gates. No manager dashboard between you and your customer. No system
            telling you how to sell.
          </p>
          <div className="grid sm:grid-cols-3 gap-4 text-sm">
            <div className="border border-slate-200 rounded-lg p-4" data-testid="manifesto-beat-1">
              <Notebook size={22} weight="fill" className="text-[#0B5394] mb-2" />
              <p className="font-bold text-slate-900 mb-1">Their exact words</p>
              <p className="text-slate-600">Every note date-stamped on the contact card, newest first. What they said is what you sell to.</p>
            </div>
            <div className="border border-slate-200 rounded-lg p-4" data-testid="manifesto-beat-2">
              <Alarm size={22} weight="fill" className="text-[#CC6600] mb-2" />
              <p className="font-bold text-slate-900 mb-1">The alarm they asked for</p>
              <p className="text-slate-600">Six months means six months. The alarm fires on the right day — not on a manager&rsquo;s cadence.</p>
            </div>
            <div className="border border-slate-200 rounded-lg p-4" data-testid="manifesto-beat-3">
              <Sparkle size={22} weight="fill" className="text-[#0B5394] mb-2" />
              <p className="font-bold text-slate-900 mb-1">A 10-second brief</p>
              <p className="text-slate-600">Crunchy summarizes everything before you dial. You walk in sounding like you never forgot.</p>
            </div>
          </div>
          <p className="leading-relaxed font-bold text-slate-900">
            Just you, your contacts, and what they actually told you.
          </p>
        </section>

        {/* CTA */}
        <div className="bg-[#0B5394] rounded-xl p-8 text-center" data-testid="manifesto-cta-card">
          <p className="text-2xl font-black text-white mb-2">Remember everything. Miss nothing.<br />No training required.</p>
          <p className="text-white/70 mb-6">$30 per user a month. No contracts. Your old CRM&rsquo;s data migrated free.</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/login"
              className="inline-flex items-center justify-center gap-2 bg-white text-[#0B5394] font-black px-6 py-3 rounded hover:bg-slate-100"
              data-testid="manifesto-cta-primary"
            >
              Try SalesCrunchCRM <ArrowRight size={18} weight="bold" />
            </Link>
            <Link
              to="/"
              className="inline-flex items-center justify-center gap-2 border border-white/40 text-white font-bold px-6 py-3 rounded hover:bg-white/10"
              data-testid="manifesto-cta-compare"
            >
              <ArrowLeft size={18} weight="bold" /> See how we stack up vs the giants
            </Link>
          </div>
        </div>
      </article>
    </div>
  );
}
